import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play } from 'lucide-react';

// Newest first, alternating video/photo
const galleryItems = [
  // --- SORORITY FORMAL (newest) ---
  { id: 1,  type: 'video', src: '/gallery/videos/formal1.mp4', caption: 'Sorority Formal', span: 'col-span-2 row-span-2' },
  { id: 2,  type: 'photo', src: '/gallery/formal1.jpg',        caption: 'Sorority Formal', span: 'col-span-1 row-span-1' },
  { id: 3,  type: 'photo', src: '/gallery/formal2.jpg',        caption: 'Sorority Formal', span: 'col-span-1 row-span-1' },
  { id: 4,  type: 'video', src: '/gallery/videos/formal2.mp4', caption: 'Sorority Formal', span: 'col-span-1 row-span-1' },
  { id: 5,  type: 'photo', src: '/gallery/formal3.jpg',        caption: 'Sorority Formal', span: 'col-span-1 row-span-1' },
  { id: 6,  type: 'video', src: '/gallery/videos/formal3.mp4', caption: 'Sorority Formal', span: 'col-span-1 row-span-1' },
  { id: 7,  type: 'photo', src: '/gallery/formal4.jpg',        caption: 'Sorority Formal', span: 'col-span-1 row-span-1' },
  { id: 8,  type: 'video', src: '/gallery/videos/formal4.mp4', caption: 'Sorority Formal', span: 'col-span-2 row-span-1' },
  // --- LED INSTALLATION (oldest) ---
  { id: 9,  type: 'photo', src: '/gallery/led1.jpg',  caption: 'Luma Installation', span: 'col-span-1 row-span-2' },
  { id: 10, type: 'photo', src: '/gallery/led2.jpg',  caption: 'Luma Installation', span: 'col-span-1 row-span-1' },
  { id: 11, type: 'photo', src: '/gallery/led3.jpg',  caption: 'Luma Installation', span: 'col-span-1 row-span-1' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.6, ease: 'easeOut' } }),
};

// Video tile — autoplays on hover
const VideoTile = ({ item, onClick }) => {
  const ref = useRef(null);
  return (
    <div
      className="relative w-full h-full overflow-hidden cursor-pointer group"
      onClick={() => onClick(item)}
      onMouseEnter={() => ref.current?.play()}
      onMouseLeave={() => { if (ref.current) { ref.current.pause(); ref.current.currentTime = 0; } }}
    >
      <video
        ref={ref}
        src={item.src}
        muted
        loop
        playsInline
        preload="metadata"
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        style={{ filter: 'brightness(0.7)' }}
      />
      {/* Play icon */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110"
          style={{ background: 'rgba(139,92,246,0.35)', border: '1px solid rgba(139,92,246,0.6)', backdropFilter: 'blur(4px)' }}
        >
          <Play size={20} className="text-white ml-1" fill="white" />
        </div>
      </div>
      {/* Caption overlay */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-5"
        style={{ background: 'linear-gradient(to top, rgba(10,10,10,0.9) 0%, transparent 60%)' }}
      >
        <span className="font-manrope text-xs text-white/70 tracking-[0.15em] uppercase">{item.caption}</span>
      </div>
      <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
        style={{ boxShadow: 'inset 0 0 0 1px rgba(139,92,246,0.4)' }} />
    </div>
  );
};

// Photo tile
const PhotoTile = ({ item, onClick }) => (
  <div
    className="relative w-full h-full overflow-hidden cursor-pointer group"
    onClick={() => onClick(item)}
  >
    <img
      src={item.src}
      alt={item.caption}
      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      style={{ filter: 'brightness(0.75)' }}
    />
    <div
      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-5"
      style={{ background: 'linear-gradient(to top, rgba(10,10,10,0.9) 0%, transparent 60%)' }}
    >
      <span className="font-manrope text-xs text-white/70 tracking-[0.15em] uppercase">{item.caption}</span>
    </div>
    <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
      style={{ boxShadow: 'inset 0 0 0 1px rgba(139,92,246,0.4)' }} />
  </div>
);

export const Gallery = () => {
  const [lightbox, setLightbox] = useState(null);

  return (
    <section id="gallery" data-testid="gallery-section"
      className="relative py-28 lg:py-36 px-6 overflow-hidden"
      style={{ background: '#0D0D0D' }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-14 lg:mb-18">
          <motion.p initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="text-xs font-manrope tracking-[0.3em] uppercase text-luma-purple mb-4">
            Gallery
          </motion.p>
          <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1}
            className="font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight text-white"
            data-testid="gallery-headline">
            Seen to be<span className="gradient-text"> believed.</span>
          </motion.h2>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 auto-rows-[200px] lg:auto-rows-[240px]">
          {galleryItems.map((item, i) => (
            <motion.div key={item.id} data-testid={`gallery-item-${item.id}`}
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }}
              variants={fadeUp} custom={i}
              className={`relative overflow-hidden rounded-xl ${item.span}`}
            >
              {item.type === 'video'
                ? <VideoTile item={item} onClick={setLightbox} />
                : <PhotoTile item={item} onClick={setLightbox} />
              }
            </motion.div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div data-testid="gallery-lightbox"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.95)' }}
            onClick={() => setLightbox(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-4xl w-full"
              onClick={e => e.stopPropagation()}
            >
              {lightbox.type === 'video' ? (
                <video
                  src={lightbox.src} controls autoPlay muted={false} loop playsInline
                  className="w-full h-auto rounded-xl"
                  style={{ maxHeight: '80vh' }}
                />
              ) : (
                <img src={lightbox.src} alt={lightbox.caption}
                  className="w-full h-auto rounded-xl"
                  style={{ maxHeight: '80vh', objectFit: 'contain' }}
                />
              )}
              <p className="font-manrope text-xs text-white/50 tracking-[0.2em] uppercase mt-4 text-center">
                {lightbox.caption}
              </p>
              <button data-testid="lightbox-close" onClick={() => setLightbox(null)}
                className="absolute -top-4 -right-4 w-10 h-10 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors duration-200">
                <X size={16} className="text-white" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
